import React from 'react';
import {
  Flex,
  GoodsCard3Column,
  GoodsCarousel,
  Headline,
  Typography,
} from '@banhala/ui-kit';
import { useCodiGoodsList } from './queries/useCodiGoodsList';
import type { CodiGoodsItem } from 'shared/src/api';
import { useLogDisplayGoods } from '../../../../hooks/useLogDisplayGoods';
import {
  useElementViewportTracking,
  useIsSectionVisible,
} from '../../store/sectionVisibility';
import {
  bridge,
  isIOS,
  isNotNil,
  LOG_ANALYTICS_ALL_CHANNELS,
  useNavigator,
} from 'shared/src/utils';
import { useRemoveLikeGoods } from '../../queries/useRemoveLikeGoods';
import type { LogAnalyticsEventParams } from 'shared/src/utils/bridge/logAnalyticsEvent/types';
import {
  useLikeMap,
  useAddLikeGoods,
  useLogAnalyticsEvent,
  useOpenDeeplink,
} from 'shared/src/hooks';
import { useGoodsSno } from '../../hooks/useGoodsSno';
import { useLogGoodsAnalyticsEvent } from '../../hooks/useLogGoodsAnalyticsEvent';
import { AdAction } from 'shared/src/utils/eventLogger/adLogger/types';

export const CodiGoodsCarousel: React.FC = () => {
  const goodsSno = useGoodsSno();
  const { data: codiGoods } = useCodiGoodsList();
  const logEvent = useLogGoodsAnalyticsEvent();
  const trackingRef = useElementViewportTracking('codi', 240);
  const isVisible = useIsSectionVisible('codi');

  const { logDisplayGoods } = useLogDisplayGoods({
    enabled: isVisible,
  });

  const { likeMap, setLikeMap } = useLikeMap(
    codiGoods?.codi_goods?.item_list.reduce((acc, val) => {
      return {
        ...acc,
        [val.item.sno]: val.item.like.is_liked,
      };
    }, {}) ?? {},
  );

  const { addLikeGoods } = useAddLikeGoods({
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'GOODS_DETAIL',
  });

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { navigator } = useNavigator();
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  if (!codiGoods?.codi_goods) return null;

  const { codi_goods } = codiGoods;
  if (codi_goods.item_list.length === 0) return null;

  return (
    <Flex ref={trackingRef}>
      <Headline
        headline={codi_goods.title}
        testID={codi_goods.title}
        size={'medium'}
        topCompact={true}
        rightPanel={
          codi_goods.item_list.length > 9 ? (
            <Flex
              onPress={() => {
                logEvent({ name: 'CLICK_COORDINATED_GOODS_MORE' });
                openDeeplink(
                  `ably://goods_list/?goods_sno=${goodsSno}&type=coordinated_goods`,
                );
              }}
            >
              <Typography type={'body2'} color={'content_secondary'}>
                전체보기
              </Typography>
            </Flex>
          ) : undefined
        }
      />
      {isIOS && codi_goods.item_list.length <= 3 ? (
        <GoodsCard3Column<CodiGoodsItem>
          keyExtractor={(item, index) => `${item.sno}-${index}`}
          list={codi_goods.item_list}
          listTestID={`${codi_goods.item_list[0]?.logging.analytics.LIST_ID}`}
          likeMap={likeMap}
          disableVirtualization={true}
          onPressItem={async (pressed, index) => {
            const { item, logging } = pressed;
            logAnalyticsEvent({
              name: 'CLICK_GOODS',
              analytics: {
                SCREEN: 'GOODS_DETAIL',
                ...logging.analytics,
                INDEX: index,
              },
              ably_only_log: logging.ably_only_log,
            });
            if (isNotNil(pressed.item.ad)) {
              bridge.logAdEvent({
                action: AdAction.CLICK,
                ad: pressed.item.ad,
              });
            }
            navigator.moveToGoods({
              sno: item.sno,
              firstPageRendering: item.first_page_rendering,
              ad: pressed.item.ad,
            });
          }}
          onPressLike={async (pressed, index) => {
            const { item, logging } = pressed;

            const analytics: LogAnalyticsEventParams['analytics'] = {
              SCREEN: 'GOODS_DETAIL',
              ...logging.analytics,
              INDEX: index,
            };

            if (!likeMap[item.sno]) {
              logAnalyticsEvent({
                name: 'CLICK_LIKE_GOODS',
                channel: LOG_ANALYTICS_ALL_CHANNELS,
                ably_only_log: logging.ably_only_log,
                analytics,
              });

              if (isNotNil(pressed.item.ad)) {
                bridge.logAdEvent({
                  action: AdAction.LIKE,
                  ad: pressed.item.ad,
                });
              }

              addLikeGoods(item.sno, {
                SCREEN: 'GOODS_DETAIL',
              });
              return;
            }
            if (isNotNil(pressed.item.ad)) {
              bridge.logAdEvent({
                action: AdAction.UNLIKE,
                ad: pressed.item.ad,
              });
            }

            logAnalyticsEvent({
              name: 'CLICK_UNLIKE_GOODS',
              ably_only_log: logging.ably_only_log,
              analytics,
            });

            await mutateRemoveLikeGoods({
              sno: item.sno,
            });

            bridge.likeGoods({
              goods_sno: item.sno,
              is_liked: false,
            });
            setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
          }}
          onViewableItemsChanged={logDisplayGoods}
        />
      ) : (
        <GoodsCarousel<CodiGoodsItem>
          keyExtractor={(item, index) => `${item.sno}-${index}`}
          list={codi_goods.item_list}
          listTestID={`${codi_goods.item_list[0]?.logging.analytics.LIST_ID}`}
          likeMap={likeMap}
          onPressItem={async (pressed, index) => {
            const { item, logging } = pressed;
            logAnalyticsEvent({
              name: 'CLICK_GOODS',
              ably_only_log: logging.ably_only_log,
              analytics: {
                SCREEN: 'GOODS_DETAIL',
                ...logging.analytics,
                INDEX: index,
              },
            });
            if (isNotNil(pressed.item.ad)) {
              bridge.logAdEvent({
                action: AdAction.CLICK,
                ad: pressed.item.ad,
              });
            }
            navigator.moveToGoods({
              sno: item.sno,
              firstPageRendering: item.first_page_rendering,
              ad: pressed.item.ad,
            });
          }}
          onPressLike={async (pressed, index) => {
            const { item, logging } = pressed;

            const analytics: LogAnalyticsEventParams['analytics'] = {
              SCREEN: 'GOODS_DETAIL',
              ...logging.analytics,
              INDEX: index,
            };

            if (!likeMap[item.sno]) {
              if (isNotNil(pressed.item.ad)) {
                bridge.logAdEvent({
                  action: AdAction.LIKE,
                  ad: pressed.item.ad,
                });
              }

              logAnalyticsEvent({
                name: 'CLICK_LIKE_GOODS',
                channel: LOG_ANALYTICS_ALL_CHANNELS,
                analytics,
                ably_only_log: pressed.logging.ably_only_log,
              });
              addLikeGoods(item.sno, {
                SCREEN: 'GOODS_DETAIL',
              });
              return;
            }
            if (isNotNil(pressed.item.ad)) {
              bridge.logAdEvent({
                action: AdAction.UNLIKE,
                ad: pressed.item.ad,
              });
            }

            logAnalyticsEvent({
              name: 'CLICK_UNLIKE_GOODS',
              channel: LOG_ANALYTICS_ALL_CHANNELS,
              analytics,
              ably_only_log: pressed.logging.ably_only_log,
            });

            await mutateRemoveLikeGoods({
              sno: item.sno,
            });

            bridge.likeGoods({
              goods_sno: item.sno,
              is_liked: false,
            });
            setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
          }}
          size={'small'}
          ratio={1.2}
          onViewableItemsChanged={logDisplayGoods}
          viewabilityConfig={{
            // Android 는 native(Compose Modifier.display) 의 weight 기반 판정과 정합 위해
            // 더 관대한 20%. iOS 는 native DisplayTracker 의 0.5 threshold 와 동일.
            itemVisiblePercentThreshold: 50,
            minimumViewTime: 500,
          }}
        />
      )}
    </Flex>
  );
};
